// src/pages/AdminTransactionsPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient.js";

import DashboardSidebarAdmin from "../components/Dashboard/DashboardSidebarAdmin.jsx";
import DashboardNavbarAdmin from "../components/Dashboard/DashboardNavbarAdmin.jsx";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const AdminTransactionsPage = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  // quick stats (hari ini / bulan ini / tahun ini)
  const [quickStats, setQuickStats] = useState({
    today: 0,
    month: 0,
    year: 0,
  });
  const [loadingQuickStats, setLoadingQuickStats] = useState(true);

  // filter & data tabel
  const [filterType, setFilterType] = useState("today"); // today | this_month | this_year | custom
  const [startDate, setStartDate] = useState(""); // yyyy-mm-dd
  const [endDate, setEndDate] = useState(""); // yyyy-mm-dd

  const [transactions, setTransactions] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);

  const [filterSummary, setFilterSummary] = useState({
    totalNet: 0,
    totalGross: 0,
    totalFee: 0,
    count: 0,
  });

  // --- helper umum ---

  const formatRupiah = (value) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    }).format(value || 0);

  const formatDateTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTodayRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    return { start, end };
  };

  const getMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return { start, end };
  };

  const getYearRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const end = new Date(now.getFullYear() + 1, 0, 1);
    return { start, end };
  };

  const getCustomRange = () => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    // supaya end date termasuk full hari → tambah 1 hari
    end.setDate(end.getDate() + 1);
    return { start, end };
  };

  const getRangeForFilter = () => {
    if (filterType === "today") return getTodayRange();
    if (filterType === "this_month") return getMonthRange();
    if (filterType === "this_year") return getYearRange();
    if (filterType === "custom") return getCustomRange();
    return null;
  };

  // ----- 1. AUTH & CEK ADMIN -----
  useEffect(() => {
    const fetchAuth = async () => {
      try {
        setLoadingAuth(true);

        const { data, error: authError } = await supabase.auth.getUser();
        const authUser = data?.user;

        if (authError || !authUser) {
          navigate("/login");
          return;
        }
        setUser(authUser);

        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", authUser.id)
          .single();

        if (profileError && profileError.code !== "PGRST116") {
          throw profileError;
        }

        if (!profileData || !profileData.phone) {
          navigate("/login");
          return;
        }

        if (profileData.role !== "admin") {
          navigate("/dashboard");
          return;
        }

        setProfile(profileData);
      } catch (err) {
        console.error("Error fetching admin auth data:", err);
      } finally {
        setLoadingAuth(false);
      }
    };

    fetchAuth();
  }, [navigate]);

  // ----- 2. Quick stats: hari ini, bulan ini, tahun ini -----
  const fetchRevenueInRange = async ({ start, end }) => {
    const { data, error } = await supabase
      .from("transactions")
      .select("amount_paid, duitku_fee, status, paid_at")
      .eq("status", "paid")
      .gte("paid_at", start.toISOString())
      .lt("paid_at", end.toISOString());

    if (error) {
      console.error("Error fetching revenue in range:", error);
      return 0;
    }

    return (data || []).reduce((sum, row) => {
      const amount = Number(row.amount_paid || 0);
      const fee = Number(row.duitku_fee || 0);
      return sum + (amount - fee);
    }, 0);
  };

  useEffect(() => {
    const fetchQuick = async () => {
      try {
        setLoadingQuickStats(true);
        const todayRange = getTodayRange();
        const monthRange = getMonthRange();
        const yearRange = getYearRange();

        const [today, month, year] = await Promise.all([
          fetchRevenueInRange(todayRange),
          fetchRevenueInRange(monthRange),
          fetchRevenueInRange(yearRange),
        ]);

        setQuickStats({ today, month, year });
      } catch (err) {
        console.error("Error fetching quick stats:", err);
      } finally {
        setLoadingQuickStats(false);
      }
    };

    if (!loadingAuth) {
      fetchQuick();
    }
  }, [loadingAuth]);

  // ----- 3. Data tabel + summary sesuai filter -----
  useEffect(() => {
    const fetchFiltered = async () => {
      try {
        const range = getRangeForFilter();
        if (!range) return;

        setLoadingTable(true);

        let query = supabase
          .from("transactions")
          .select(
            "id, merchant_order_id, user_id, package_id, amount_paid, duitku_fee, status, paid_at, payment_channel",
            { count: "exact" }
          )
          .eq("status", "paid")
          .gte("paid_at", range.start.toISOString())
          .lt("paid_at", range.end.toISOString())
          .order("paid_at", { ascending: false });

        const { data, error, count } = await query;

        if (error) {
          console.error("Error fetching filtered transactions:", error);
          setTransactions([]);
          setFilterSummary({ totalNet: 0, totalGross: 0, totalFee: 0, count: 0 });
          return;
        }

        // Ambil profil user
        let transactionsWithProfiles = data || [];
        if (data && data.length > 0) {
          const userIds = [...new Set(data.map((tx) => tx.user_id).filter(Boolean))];
          if (userIds.length > 0) {
            const { data: profilesData } = await supabase
              .from("profiles")
              .select("id, username, email")
              .in("id", userIds);

            const profileMap = {};
            (profilesData || []).forEach((p) => {
              profileMap[p.id] = p;
            });

            transactionsWithProfiles = data.map((tx) => ({
              ...tx,
              profile: profileMap[tx.user_id] || null,
            }));
          }
        }

        const { totalNet, totalGross, totalFee } = transactionsWithProfiles.reduce(
          (acc, row) => {
            const amount = Number(row.amount_paid || 0);
            const fee = Number(row.duitku_fee || 0);
            acc.totalGross += amount;
            acc.totalNet += amount - fee;
            acc.totalFee += fee;
            return acc;
          },
          { totalNet: 0, totalGross: 0, totalFee: 0 }
        );

        setTransactions(transactionsWithProfiles);
        setFilterSummary({
          totalNet,
          totalGross,
          totalFee,
          count: count ?? (data ? data.length : 0),
        });
      } catch (err) {
        console.error("Error in fetchFiltered:", err);
      } finally {
        setLoadingTable(false);
      }
    };

    if (!loadingAuth) {
      if (filterType === "custom") {
        if (!startDate || !endDate) return;
      }
      fetchFiltered();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, startDate, endDate, loadingAuth]);

  const displayName =
    profile?.username || user?.email?.split("@")[0] || "Admin";

  const monthName = useMemo(
    () =>
      new Date().toLocaleDateString("id-ID", {
        month: "long",
        year: "numeric",
      }),
    []
  );

  // ---------- Download PDF ----------
  const handleDownloadPdf = () => {
    if (!transactions.length) return;

    const doc = new jsPDF("p", "mm", "a4");
    doc.setFontSize(14);
    doc.text("Transaksi Paid", 14, 16);

    let subtitle = "Filter: semua transaksi paid";
    if (filterType === "today") {
      subtitle = "Filter: Hari ini";
    } else if (filterType === "this_month") {
      subtitle = `Filter: Bulan ini (${monthName})`;
    } else if (filterType === "this_year") {
      subtitle = `Filter: Tahun ${new Date().getFullYear()}`;
    } else if (filterType === "custom") {
      subtitle = `Filter: ${startDate || "-"} s/d ${endDate || "-"}`;
    }

    doc.setFontSize(10);
    doc.text(subtitle, 14, 22);

    // siapkan body tabel
    const rows = transactions.map((tx, idx) => {
      const gross = Number(tx.amount_paid || 0);
      const fee = Number(tx.duitku_fee || 0);
      const net = gross - fee;
      const userName = tx.profile?.username || tx.profile?.email || tx.user_id || "-";

      return [
        idx + 1,
        formatDateTime(tx.paid_at),
        `${tx.merchant_order_id || "-"}\n(${userName})`,
        tx.payment_channel || "-",
        formatRupiah(gross),
        formatRupiah(fee),
        formatRupiah(net),
      ];
    });

    // hitung total net untuk footer
    const totals = transactions.reduce(
      (acc, tx) => {
        const gross = Number(tx.amount_paid || 0);
        const fee = Number(tx.duitku_fee || 0);
        acc.net += gross - fee;
        return acc;
      },
      { net: 0 }
    );

    autoTable(doc, {
      head: [["No", "Tanggal", "Order ID", "Channel", "Gross", "Fee", "Net"]],
      body: rows,
      foot: [
        [
          "", // No
          "", // Tanggal
          "", // Order ID
          "", // Channel
          "", // Gross
          "TOTAL", // Fee → label
          formatRupiah(totals.net), // Net → hasil total
        ],
      ],
      startY: 26,
      styles: { fontSize: 8, cellWidth: "wrap" },

      // Header biru tua, teks putih (seperti screenshot)
      headStyles: {
        fillColor: [10, 60, 120], // navy-ish
        textColor: 255,
        fontStyle: "bold",
      },

      // Row zebra lembut
      alternateRowStyles: {
        fillColor: [244, 247, 252], // abu/biru sangat muda
      },

      // Footer TOTAL dengan warna pudar (lebih lembut dari header)
      footStyles: {
        fillColor: [230, 237, 247], // biru muda pudar
        textColor: 0,
        fontStyle: "bold",
      },
    });

    doc.save("transaksi-paid.pdf");
  };

  if (loadingAuth) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mb-3" />
        <p className="text-slate-500">Memuat data admin...</p>
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-50 font-sans overflow-hidden">
      <div className="flex h-full min-w-0">
        {/* SIDEBAR ADMIN (fixed, tidak ikut scroll konten) */}
        <DashboardSidebarAdmin />

        {/* AREA KANAN */}
        <div className="flex-1 flex flex-col min-w-0 h-full">
          <DashboardNavbarAdmin displayName={displayName} />

          {/* KONTEN: scroll sendiri */}
          <main className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Header */}
              <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-5 sm:p-6">
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
                  Transaksi & Penghasilan
                </h1>
                <p className="text-sm sm:text-base text-slate-600">
                  Pantau transaksi yang sudah{" "}
                  <span className="font-semibold">paid</span> dan lihat
                  penghasilan harian, bulanan, tahunan, atau range khusus.
                </p>
              </div>

              {/* Quick stats */}
              <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <QuickStatCard
                  title="Hari ini"
                  subtitle="Total net"
                  value={
                    loadingQuickStats
                      ? "Memuat..."
                      : formatRupiah(quickStats.today)
                  }
                />
                <QuickStatCard
                  title={`Bulan ini · ${monthName}`}
                  subtitle="Total net"
                  value={
                    loadingQuickStats
                      ? "Memuat..."
                      : formatRupiah(quickStats.month)
                  }
                />
                <QuickStatCard
                  title={`Tahun ${new Date().getFullYear()}`}
                  subtitle="Total net"
                  value={
                    loadingQuickStats
                      ? "Memuat..."
                      : formatRupiah(quickStats.year)
                  }
                />
              </section>

              {/* Filter */}
              <section className="bg-white rounded-2xl shadow-sm border border-slate-200/70 p-4 sm:p-5 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-semibold text-slate-900">
                      Filter transaksi
                    </h2>
                    <p className="text-xs text-slate-500">
                      Penghasilan di bawah akan mengikuti filter ini.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        className="text-xs sm:text-sm border border-slate-200 rounded-xl px-3 py-2 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
                      >
                        <option value="today">Hari ini</option>
                        <option value="this_month">Bulan ini</option>
                        <option value="this_year">Tahun ini</option>
                        <option value="custom">Custom range</option>
                      </select>

                      {filterType === "custom" && (
                        <>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="text-xs sm:text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
                          />
                          <span className="text-xs text-slate-400">
                            sampai
                          </span>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="text-xs sm:text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
                          />
                        </>
                      )}
                    </div>

                    {/* Tombol Download PDF */}
                    <button
                      type="button"
                      onClick={handleDownloadPdf}
                      disabled={transactions.length === 0}
                      className="inline-flex items-center justify-center px-3 py-2 rounded-xl bg-sky-600 text-white text-xs sm:text-sm font-semibold shadow-sm hover:bg-sky-700 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      📄 Download PDF
                    </button>
                  </div>
                </div>

                {/* Ringkasan untuk filter */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="border border-slate-200 rounded-xl p-3 flex flex-col">
                    <span className="text-[11px] text-slate-500 mb-1">
                      Total penghasilan (net) untuk filter ini
                    </span>
                    <span className="text-base font-semibold text-sky-700">
                      {loadingTable
                        ? "Menghitung..."
                        : formatRupiah(filterSummary.totalNet)}
                    </span>
                  </div>
                  <div className="border border-slate-200 rounded-xl p-3 flex flex-col">
                    <span className="text-[11px] text-slate-500 mb-1">
                      Total nominal dibayar (gross)
                    </span>
                    <span className="text-base font-semibold text-slate-800">
                      {loadingTable
                        ? "Menghitung..."
                        : formatRupiah(filterSummary.totalGross)}
                    </span>
                  </div>
                  <div className="border border-slate-200 rounded-xl p-3 flex flex-col">
                    <span className="text-[11px] text-slate-500 mb-1">
                      Jumlah transaksi (paid)
                    </span>
                    <span className="text-base font-semibold text-emerald-700">
                      {loadingTable ? "..." : filterSummary.count}
                    </span>
                  </div>
                </div>
              </section>

              {/* Tabel transaksi */}
              <section className="bg-white rounded-2xl shadow-sm border border-slate-200/70 p-4 sm:p-5">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-slate-900">
                    Daftar transaksi (paid)
                  </h2>
                  <span className="text-[11px] text-slate-500">
                    Menampilkan {transactions.length} data
                  </span>
                </div>

                <div className="overflow-x-auto -mx-3 sm:mx-0">
                  <table className="min-w-full text-xs sm:text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/60">
                        <th className="px-3 py-2 text-left font-semibold text-slate-600">
                          Tanggal bayar
                        </th>
                        <th className="px-3 py-2 text-left font-semibold text-slate-600">
                          Order ID
                        </th>
                        <th className="px-3 py-2 text-right font-semibold text-slate-600">
                          Gross
                        </th>
                        <th className="px-3 py-2 text-right font-semibold text-slate-600">
                          Fee
                        </th>
                        <th className="px-3 py-2 text-right font-semibold text-slate-600">
                          Net
                        </th>
                        <th className="px-3 py-2 text-left font-semibold text-slate-600">
                          Channel
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingTable ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-3 py-6 text-center text-slate-400"
                          >
                            Memuat transaksi...
                          </td>
                        </tr>
                      ) : transactions.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-3 py-6 text-center text-slate-400"
                          >
                            Belum ada transaksi paid pada range ini.
                          </td>
                        </tr>
                      ) : (
                        transactions.map((tx) => {
                          const gross = Number(tx.amount_paid || 0);
                          const fee = Number(tx.duitku_fee || 0);
                          const net = gross - fee;
                          const tanggal = formatDateTime(tx.paid_at);

                          return (
                            <tr
                              key={tx.id}
                              className="border-b border-slate-100/80 hover:bg-slate-50/60"
                            >
                              <td className="px-3 py-2 align-top text-slate-700">
                                {tanggal}
                              </td>
                              <td className="px-3 py-2 align-top text-slate-700">
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {tx.merchant_order_id}
                                  </span>
                                  <span className="text-[11px] text-slate-500">
                                    User: {tx.profile?.username || tx.profile?.email || tx.user_id || "-"}
                                  </span>
                                </div>
                              </td>
                              <td className="px-3 py-2 align-top text-right text-slate-700">
                                {formatRupiah(gross)}
                              </td>
                              <td className="px-3 py-2 align-top text-right text-slate-500">
                                {formatRupiah(fee)}
                              </td>
                              <td className="px-3 py-2 align-top text-right font-semibold text-emerald-700">
                                {formatRupiah(net)}
                              </td>
                              <td className="px-3 py-2 align-top text-slate-600">
                                {tx.payment_channel || "-"}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

const QuickStatCard = ({ title, subtitle, value }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col justify-between shadow-sm">
    <div>
      <div className="text-[11px] font-medium text-slate-500 mb-1">
        {title}
      </div>
      <div className="text-sm text-slate-400 mb-2">{subtitle}</div>
    </div>
    <div className="text-base sm:text-lg font-semibold text-sky-700">
      {value}
    </div>
  </div>
);

export default AdminTransactionsPage;
